// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useMemo, useState } from 'react';
import { montecarloApi } from '@advisor-app/store/api/ai/montecarlo-api';
import { numberToFixed } from '@shared/ui-kit/utils';
import { useTranslation } from 'react-i18next';
import {
  type PortfolioAnalysis,
  type RiskReturns,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { useConst, useSafeUpdate } from '@shared/ui-kit/hooks';
import {
  type MontecarloSimulationPayload,
  type MontecarloSimulationResp,
} from '@shared/data-access/interfaces/api/ai/montecarlo';
import { setCopilotSimulationInsightPayload } from '@advisor-app/store/slices/copilot-buffer';
import { useDispatch } from 'react-redux';
import { type SimulationPopupName } from '@shared/ui-kit/components/info-popup/data/simulation';

export type parameterKey =
  | 'initialAmount'
  | 'expectedReturn'
  | 'expectedVolatility'
  | 'period'
  | 'deposit'
  | 'riskFreeRate'
  | 'scenarios';

interface UseSimulationProps {
  analysis: PortfolioAnalysis;
}

interface SimulationParameter {
  name: string;
  value: number;
  onChangeFunction: (key: parameterKey, value: number) => void;
  inputKey: parameterKey;
  symbol: string;
  max: number;
  min?: number;
  popup?: SimulationPopupName;
}

export const useSimulation = ({ analysis }: UseSimulationProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const runSafeUpdate = useSafeUpdate();
  const [montecarloSimulation] = montecarloApi.useLazySimulationQuery();

  const defaultAmount = analysis.capital_gains.outstanding_balance;

  const riskReturnPortfolio = analysis.risk_returns.find(
    (rr: RiskReturns) => rr.ticker === 'Portfolio',
  );

  const { returns, volatility } = riskReturnPortfolio!;

  const fixedAmount = Math.round(defaultAmount);
  const fixedReturns = numberToFixed(returns * 100);
  const fixedVolatility = numberToFixed(volatility * 100);

  const [loading, setLoading] = useState(false);

  const [simulation, setSimulation] = useState<MontecarloSimulationResp[]>([]);

  const [config, setConfig] = useState({
    chartAmount: fixedAmount,
    initialAmount: fixedAmount,
    expectedReturn: fixedReturns,
    expectedVolatility: fixedVolatility,
    period: 20,
    deposit: 0,
    riskFreeRate: 2.5,
    scenarios: 2000,
  });

  const initialValues = useConst(() => [config]);

  const handleParameterChange = useCallback(
    (key: keyof typeof config, value: number) => {
      setConfig(prevState => ({ ...prevState, [key]: value }));
    },
    [],
  );

  const simulationParameters: SimulationParameter[] = useMemo(
    () => [
      {
        name: t('simulation.initial-amount', { ns: 'components' }) as string,
        value: config.initialAmount,
        onChangeFunction: handleParameterChange,
        inputKey: 'initialAmount',
        symbol: '',
        max: 10000000,
      },
      {
        name: t('simulation.expected-return', { ns: 'components' }) as string,
        value: config.expectedReturn,
        onChangeFunction: handleParameterChange,
        inputKey: 'expectedReturn',
        symbol: '%',
        max: 100,
        popup: 'expectedReturns',
      },
      {
        name: t('simulation.expected-volatility', { ns: 'components' }) as string,
        value: config.expectedVolatility,
        onChangeFunction: handleParameterChange,
        inputKey: 'expectedVolatility',
        symbol: '%',
        max: 100,
        popup: 'expectedVolatility',
      },
      {
        name: t('simulation.period-years', { ns: 'components' }) as string,
        value: config.period,
        onChangeFunction: handleParameterChange,
        inputKey: 'period',
        symbol: '',
        max: 100,
      },
      {
        name: t('simulation.annual-deposit-withdrawal', {
          ns: 'components',
        }) as string,
        value: config.deposit,
        onChangeFunction: handleParameterChange,
        inputKey: 'deposit',
        symbol: '',
        max: 10000000,
        min: -10000000,
        popup: 'annualDepositWithdrawal',
      },
      {
        name: t('simulation.risk-free-rate', { ns: 'components' }) as string,
        value: config.riskFreeRate,
        onChangeFunction: handleParameterChange,
        inputKey: 'riskFreeRate',
        symbol: '%',
        max: 100,
      },
      {
        name: t('simulation.number-of-scenarios', { ns: 'components' }) as string,
        value: config.scenarios,
        onChangeFunction: handleParameterChange,
        inputKey: 'scenarios',
        symbol: '',
        max: 1000000,
        popup: 'numberOfScenarios',
      },
    ],
    [config, handleParameterChange, t],
  );

  const handleReset = useCallback(() => {
    setSimulation([]);
    setConfig(initialValues[0]);
  }, [initialValues]);

  const handleAnalyze = useCallback(async () => {
    setLoading(true);
    try {
      const {
        initialAmount,
        expectedReturn,
        expectedVolatility,
        period,
        deposit,
        riskFreeRate,
        scenarios,
      } = config;

      const payload: MontecarloSimulationPayload = {
        expected_returns: expectedReturn / 100,
        expected_volatility: expectedVolatility / 100,
        agg_period: 1,
        initial_amount: initialAmount,
        cashflow_contribution: deposit,
        n_periods: period,
        n_scenarios: scenarios,
        quantiles: [0.1, 0.25, 0.5, 0.75, 0.9],
        risk_free_rate: riskFreeRate / 100,
      };

      const resp = await montecarloSimulation({ payload }).unwrap();

      runSafeUpdate(() => {
        setSimulation(resp);
        setConfig(prevState => ({ ...prevState, chartAmount: initialAmount }));
        dispatch(
          setCopilotSimulationInsightPayload({
            montecarlo_settings: payload,
            montecarlo_quantiles: resp,
          }),
        );
      });
    } catch (error) {
      console.error('[useSimulation]', error);
    }
    setLoading(false);
  }, [config, runSafeUpdate, dispatch, montecarloSimulation]);

  return {
    handleAnalyze,
    handleReset,
    handleParameterChange,
    simulationParameters,
    simulation,
    loading,
    ...config,
  };
};
